import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { sum } from 'lodash'

import { getCurrentDate } from '@/helpers/dates'
import { prepareResponseError } from '../utils/helpers'
import { handleCatchedError } from '@/helpers/common'

import api from '@/store/api'

import { useRepositoriesStore } from '../repositories'

export const useAdvisorStore = defineStore('advisor', () => {
  // INIT
  const loading = ref(false)
  const selectedClient = ref<string>()

  const totals = ref<Map<string, number>>(new Map())

  let abortController = new AbortController()

  const repositoriesStore = useRepositoriesStore()

  // GETTERS
  const getSelectedClient = computed(() => selectedClient.value)

  const getClients = computed(() => repositoriesStore.getList)
  const getTotals = computed(() => totals.value)

  const getTotal = computed(() => {
    if (selectedClient.value) {
      return totals.value.get(selectedClient.value) || 0
    }
    return sum(Array.from(totals.value.values()))
  })

  // SETTERS
  const setSelectedClient = (clientId?: string) => {
    selectedClient.value = clientId
  }

  const setTotal = (clientId: string, total: number) => {
    totals.value.set(clientId, total)
  }

  // ACTIONS
  const fetchRepoData = async (clientId: string) => {
    const params = {
      periods: 1,
      freq: 'day',
      end: getCurrentDate({}),
      end_is_today: false,
      include_end: true,
      data: 'tree',
    }
    try {
      const result = await api.get(
        `repositories/${clientId}/analytics`,
        {
          params,
          signal: abortController.signal,
        },
      )
      const treeView = result.data.tree_view
      setTotal(clientId, treeView?.[0].balance || 0)
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const fetchClients = async () => {
    loading.value = true
    const promises: Promise<any>[] = []
    repositoriesStore.getList.forEach(repository => {
      promises.push(fetchRepoData(repository.id))
    })
    try {
      await Promise.all(promises)
    } catch (e) {
      handleCatchedError(e as string, { context: 'Fetching clients' })
    } finally {
      loading.value = false
    }
  }

  const cancel = () => {
    abortController.abort()
    abortController = new AbortController()
  }

  return {
    loading,

    fetchClients,

    setSelectedClient,
    getSelectedClient,

    getClients,
    getTotals,
    getTotal,

    cancel,
  }
})
